/**
 * Serializes menu selection saves and coalesces rapid changes so the latest
 * payload is what ends up written on the server (and applied in the UI).
 */
export type MenuSelectionPipeline<TPayload> = {
  enqueue: (payload: TPayload) => Promise<void>;
  invalidate: () => void;
  waitForIdle: () => Promise<void>;
  getRequestId: () => number;
};

export function createMenuSelectionPipeline<TPayload, TResponse>(options: {
  save: (payload: TPayload) => Promise<TResponse>;
  onResponse: (response: TResponse, requestId: number) => void;
  onError: (error: unknown, requestId: number) => void;
}): MenuSelectionPipeline<TPayload> {
  let requestId = 0;
  let pendingPayload: TPayload | null = null;
  let pipeline: Promise<void> = Promise.resolve();

  const enqueue = (payload: TPayload): Promise<void> => {
    requestId += 1;
    pendingPayload = payload;

    const run = async () => {
      while (pendingPayload !== null) {
        const dataToSend = pendingPayload;
        const activeRequestId = requestId;
        pendingPayload = null;

        try {
          const response = await options.save(dataToSend);
          options.onResponse(response, activeRequestId);
        } catch (error) {
          options.onError(error, activeRequestId);
        }
      }
    };

    pipeline = pipeline.then(run).catch(() => undefined);
    return pipeline;
  };

  const invalidate = () => {
    requestId += 1;
    pendingPayload = null;
  };

  return {
    enqueue,
    invalidate,
    waitForIdle: () => pipeline,
    getRequestId: () => requestId,
  };
}
